import React, { useState } from 'react';
import { GSDocumentToolNavbar } from '@/components/layout/GSDocumentToolNavbar';
import { usePortfolios } from '@/hooks/usePortfolios';
import { PortfolioCard } from '@/components/portfolio/PortfolioCard';
import { CreatePortfolioDialog } from '@/components/portfolio/CreatePortfolioDialog';
import { EmptyPortfolioState } from '@/components/portfolio/EmptyPortfolioState';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle 
} from '@/components/ui/alert-dialog';
import { PortfolioType } from '@/types/portfolio';
import { usePersonaContext } from '@/contexts/PersonaContext';

const GSCertClient = () => {
  const { currentPersona } = usePersonaContext();
  const { portfolios, isLoading, error, createPortfolio, deletePortfolio } = usePortfolios();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [portfolioToDelete, setPortfolioToDelete] = useState<string | null>(null);

  const handleCreatePortfolio = (portfolioData: { 
    title: string; 
    description: string; 
    portfolio_type: PortfolioType;
  }) => {
    createPortfolio.mutate({
      title: portfolioData.title,
      description: portfolioData.description,
      portfolio_type: portfolioData.portfolio_type,
      status: 'active'
    });
  };

  const handleDeletePortfolio = (id: string) => {
    setPortfolioToDelete(id);
  };

  const confirmDelete = () => {
    if (portfolioToDelete) {
      deletePortfolio.mutate(portfolioToDelete);
      setPortfolioToDelete(null);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <GSDocumentToolNavbar />
      
      <main className="flex-1 container py-12">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold">My Portfolios</h1>
            <p className="text-muted-foreground">
              Manage your products and their certification progress
            </p>
          </div>
          {portfolios && portfolios.length > 0 && (
            <CreatePortfolioDialog onCreate={handleCreatePortfolio} />
          )}
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <p>Loading portfolios...</p>
          </div>
        ) : error ? (
          <div className="bg-destructive/10 text-destructive p-4 rounded-md">
            An error occurred: {(error as Error).message}
          </div>
        ) : portfolios && portfolios.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {portfolios.map((portfolio) => (
              <PortfolioCard
                key={portfolio.id}
                portfolio={portfolio}
                onDelete={handleDeletePortfolio}
                basePath="/gs-cert-client"
              />
            ))}
          </div>
        ) : (
          <EmptyPortfolioState onCreateClick={() => setDialogOpen(true)} />
        )}
      </main>

      <AlertDialog open={!!portfolioToDelete} onOpenChange={() => setPortfolioToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the portfolio and all its items.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GSCertClient;